/**
 * A task's place in capability linkage (Feature 71, 6.3 and 7.3). On a first
 * task, one read-only canvas per run: the template, whether the run is open
 * and until when, and every step colored by where it stands; a step that
 * ran opens its task. On a linked task, the step it ran as and the first
 * task it belongs to. Both read `GET /tasks/{id}/linkage`, which answers a
 * linked task with its first task's runs. The runs are polled while one is
 * open.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { getTask, getTaskLinkage } from '@optum/api-client/endpoints/tasks';
import { listLinkageTemplates } from '@optum/api-client/endpoints/linkage-templates';
import type { ForkedStep, LinkageRun, Task, TaskLinkage } from '@optum/api-client/types';

import { slotText, viewerTimeZone } from '../../lib/cron.js';
import { absoluteDateTime, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useCurrentOrgId } from '../../lib/use-current-org.js';
import { FlowCanvas } from '../../ui/FlowCanvas.js';
import { Badge, Inline, Stack, Typography } from '../../ui/finixt.js';
import type { Edge, EdgeTypes, NodeTypes } from '../../ui/flow.js';
import { RulesEdge, type RulesEdgeType } from './RulesEdge.js';
import { RETURN_IN, RETURN_OUT } from './StepNode.js';
import { WriteBackEdge, type WriteBackEdgeType } from './WriteBackEdge.js';
import { RunNode, type RunNodeType } from './RunNode.js';
import { RunConditionNode, type RunConditionNodeType } from './RunConditionNode.js';
import { RunGateNode, type RunGateNodeType } from './RunGateNode.js';
import { canvasHeight, runFlow, runOf, runStartWords, runsText } from './run-flow.js';

const NODE_TYPES: NodeTypes = {
  run: RunNode,
  runGate: RunGateNode,
  runCondition: RunConditionNode,
};
// A gate's ties to its condition nodes, pale as on the editor's canvas.
const TIE_STYLE = { stroke: 'var(--blue)', strokeOpacity: 0.35, strokeWidth: 2 };
const EDGE_TYPES: EdgeTypes = { rules: RulesEdge, writeBack: WriteBackEdge };
const OPEN_POLL_MS = 3_000;

function noop(): void {}

/** The runs of the task's chain, polled while one is open. */
export function useTaskLinkage(taskId: string) {
  return useQuery({
    queryKey: qk.taskLinkage(taskId),
    queryFn: () => getTaskLinkage(taskId),
    refetchInterval: (query) =>
      query.state.data?.runs.some((r) => r.status === 'open') ? OPEN_POLL_MS : false,
  });
}

/** The tasks path under the tenant when the page is under one. */
function useTaskPath(): (taskId: string) => string {
  const scopedOrgId = useCurrentOrgId();
  return (taskId) => `${scopedOrgId ? `/orgs/${scopedOrgId}` : ''}/tasks/${taskId}`;
}

function RunView({
  run,
  name,
  firstCapabilityId,
  focus,
}: {
  run: LinkageRun;
  name: string | undefined;
  firstCapabilityId: string;
  /** An independent step whose subtree the page is about. */
  focus?: string | undefined;
}) {
  const navigate = useNavigate();
  const taskPath = useTaskPath();
  const flow = runFlow(run, firstCapabilityId, focus);
  const nodes: (RunNodeType | RunGateNodeType | RunConditionNodeType)[] = [
    ...flow.nodes.map((n): RunNodeType => ({ ...n, type: 'run' })),
    ...flow.gates.map((g): RunGateNodeType => ({ ...g, type: 'runGate' })),
    ...flow.conditions.map((c): RunConditionNodeType => ({ ...c, type: 'runCondition' })),
  ];
  const edges: (RulesEdgeType | WriteBackEdgeType | Edge)[] = [
    ...flow.edges.map((e): RulesEdgeType => ({ ...e, type: 'rules' })),
    // A gate's way in, a plain line as on the editor's canvas (Feature 79).
    ...flow.ways.map(
      (w): Edge => ({
        ...w,
        type: 'smoothstep',
        style: { stroke: 'var(--border-hover)', strokeWidth: 2 },
      }),
    ),
    ...flow.ties.map((tie): Edge => ({ ...tie, type: 'smoothstep', style: TIE_STYLE })),
    ...flow.returns.map(
      (r): WriteBackEdgeType => ({
        ...r,
        type: 'writeBack',
        sourceHandle: RETURN_OUT,
        targetHandle: RETURN_IN,
      }),
    ),
  ];
  const open = run.status === 'open';
  return (
    <Stack gap={7}>
      <Inline gap={9} align="center">
        <span className="mono" style={{ fontWeight: 'var(--weight-semibold)' }}>
          {run.template_key}
        </span>
        {name && <Typography variant="meta">{name}</Typography>}
        <Typography variant="meta">{runStartWords(run)}</Typography>
        <Badge tone={open ? 'info' : 'neutral'}>{run.status}</Badge>
        <Typography variant="meta">
          {open ? 'deadline ' : 'ended '}
          <span className="mono">
            {absoluteDateTime(open ? run.deadline_at : (run.ended_at ?? run.deadline_at))}
          </span>
        </Typography>
      </Inline>
      <FlowCanvas<
        RunNodeType | RunGateNodeType | RunConditionNodeType,
        RulesEdgeType | WriteBackEdgeType | Edge
      >
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        height={canvasHeight(
          [...flow.nodes, ...flow.gates, ...flow.conditions],
          flow.returns.length > 0,
        )}
        onNodesChange={noop}
        onConnect={noop}
        isValidConnection={() => false}
        onNodeClick={(_event, node) => {
          // A gate or a condition is no task: only a step that ran opens one.
          const taskId = node.type === 'run' ? (node as RunNodeType).data.taskId : null;
          if (taskId) navigate(taskPath(taskId));
        }}
        onEdgeClick={noop}
        onPaneClick={noop}
        readOnly
      />
    </Stack>
  );
}

/** One canvas per run of a first task, oldest run first as the agent lists them. */
export function TaskRuns({ task, linkage }: { task: Task; linkage: TaskLinkage }) {
  const templates = useQuery({
    queryKey: qk.linkageTemplates(),
    queryFn: listLinkageTemplates,
  });
  const names = new Map((templates.data ?? []).map((t) => [t.key, t.name]));
  return (
    <Stack gap={18}>
      <Typography variant="meta">{runsText(linkage.runs)}</Typography>
      {linkage.runs.map((run) => (
        <RunView
          key={run.template_key}
          run={run}
          name={names.get(run.template_key)}
          firstCapabilityId={task.capability_id}
        />
      ))}
    </Stack>
  );
}

/** An independent task's own run: the run it belongs to, its subtree drawn and the rest dimmed. */
export function IndependentRun({ task, linkage }: { task: Task; linkage: TaskLinkage }) {
  const run = runOf(linkage, task.id);
  // A refused fire's run has no first task to read the capability of (Feature 80 D11).
  const firstId = linkage.first_task_id;
  const first = useQuery({
    queryKey: qk.task(firstId ?? ''),
    queryFn: () => getTask(firstId ?? ''),
    enabled: firstId !== null,
  });
  if (!run || !task.linkage_step) return null;
  return (
    <RunView
      run={run}
      name={undefined}
      firstCapabilityId={first.data?.capability_id ?? ''}
      focus={task.linkage_step}
    />
  );
}

/** The steps a call forked as it was accepted, each linking its task (Feature 78 D17). */
export function ForkedSteps({ forked }: { forked: readonly ForkedStep[] }) {
  const taskPath = useTaskPath();
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {forked.map((f) => (
        <span key={f.task_id}>
          <span className="mono">{f.step}</span> →{' '}
          <Link to={taskPath(f.task_id)} className="mono">
            {shortId(f.task_id)}
          </Link>{' '}
          · {f.mode} · <span className="mono">{f.template}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * A linked task's step, its template, and links to its first task and, when
 * its parent is an independent task between them, to that task too (D59).
 * A schedule run's first task is the one its fire submitted, and the slot
 * it fired for stands beside it (Feature 80 D12). A call run's first task is
 * the call its step was forked from (Feature 78 D16).
 */
export function LinkedFrom({ task, linkage }: { task: Task; linkage: TaskLinkage | undefined }) {
  const taskPath = useTaskPath();
  const run = linkage ? runOf(linkage, task.id) : undefined;
  const scheduled = run?.start_kind === 'schedule';
  const first = linkage?.first_task_id ?? task.parent_task_id;
  const parent = task.parent_task_id;
  return (
    <span>
      {task.linkage_mode === 'independent' ? 'independent step ' : 'step '}
      <span className="mono">{task.linkage_step}</span>
      {run && (
        <>
          {' '}
          of <span className="mono">{run.template_key}</span>
        </>
      )}
      {parent && parent !== first && (
        <>
          {' · under '}
          <Link to={taskPath(parent)} className="mono">
            {shortId(parent)}
          </Link>
        </>
      )}
      {first && (
        <>
          {run?.start_kind === 'call' ? ' · forked from ' : ' · first task '}
          <Link to={taskPath(first)} className="mono">
            {shortId(first)}
          </Link>
        </>
      )}
      {scheduled && (
        <>
          {' · fired for '}
          {run?.scheduled_for
            ? slotText(new Date(run.scheduled_for), viewerTimeZone())
            : 'a schedule'}
        </>
      )}
    </span>
  );
}
